import './style/ShipmentDetails.css';
import React, { useCallback } from 'react';
import Button from './Button';
import { useSelector } from 'react-redux';
import { useDispatch } from 'react-redux';
import { markShipped } from '../../redux/keystoneInterfaceSlice';
import { useNavigate, useParams } from 'react-router-dom';
import { Text, Button as NewButton, Navigate } from '@harperai/materialize-design-system';
import { formatDisplayNumber, formatNumberWithCommas } from '../../util/Calculate';
import LogoBlack from '../../media/materialize-logo-black@3x.png';
import Divider from './Divider';

const ShipmentDetails = ({ handleAccept, handleReject }) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const keystoneTrayData = useSelector((state) => state?.keystone?.trayData);
    const keystoneTrayType = useSelector((state) => state?.keystone?.trayType);
    const keystoneInterfaceAccessToken = useSelector((state) => state?.keystoneInterface?.accessToken);

    const { page, tabName } = useParams();

    const handleMarkAsShipped = (shipmentId) => {
        dispatch(markShipped({
            accessToken: keystoneInterfaceAccessToken,
            id: shipmentId,
        }));
        navigate(`/${page}`);
    }

    const handleCloseTray = () => {
        navigate(`/${page}/${tabName}`);
    };

    const totalItems = keystoneTrayData?.related_offer_items?.reduce((acc, item) => {
        return acc + item.quantity;
    }, 0);

    const downloadFile = useCallback(async (id) => {
        const accessToken = keystoneInterfaceAccessToken;
        const getApiBaseUrl = () => {
            return window.location.origin.includes('local') ? 'http://localhost:8080/keystone' : 'https://api.trymaterialize.com/keystone';
        }
        const fileUrl = `${getApiBaseUrl()}/shipments/tracking/${id}/shipping_label`; 
        
        try {
            const response = await fetch(fileUrl, {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${accessToken}`,
            },
            });

            if (!response.ok) {
            throw new Error('Network response was not ok');
            }

            const reader = response.body.getReader();
            
            const stream = new ReadableStream({
            start(controller) {
                function push() {
                reader.read().then(({ done, value }) => {
                    if (done) {
                    controller.close();
                    return;
                    }
                    controller.enqueue(value);
                    push();
                });
                }
                
                push();
            }
            });

            const dlUrl = URL.createObjectURL(await new Response(stream).blob());

            const link = document.createElement('a');
            link.href = dlUrl;

            const contentDisposition = response.headers.get('content-disposition');
            let filename = 'materialize-shipping_label'; // Fallback filename
            
            if (contentDisposition) {
            const filenameMatch = contentDisposition.match(/filename="(.+)"/) || contentDisposition.match(/filename=([^;]+)/);
            if (filenameMatch && filenameMatch.length > 1) {
                filename = `${filenameMatch[1]}`;
            }
            }

            link.setAttribute('download', filename);

            document.body.appendChild(link);
            link.click();
            link.remove();

            window.URL.revokeObjectURL(dlUrl);

        } catch (error) {
            console.error('Streaming download failed:', error);
        }
    }, [keystoneInterfaceAccessToken]);

    const PickList = () => {
        return (
            <div className="only-print">
                <div className="flex vertical gap-xl height-100">
                    <div className="flex vertical gap-s">
                        <Text size="h1" bold={true}>Pick List</Text>
                        <div className="flex vertical gap-xs">
                            <Text size="paragraph" mono={true}>Shipment: {formatDisplayNumber(keystoneTrayData?.shipment_number)}</Text>
                            <Text size="paragraph" mono={true}>Warehouse: {keystoneTrayData?.warehouse_name}</Text>
                        </div>
                    </div>
                    <div className="flex vertical gap-s" style={{flexBasis: "1px", flexGrow: "1"}}>
                        <div className="flex horizontal align-start">
                            <div className="flex vertical height-100">
                                <Text size="caption" mono={true} bold={true} classes="pad-xs border-bottom border-right">MFG</Text>
                                {keystoneTrayData?.related_offer_items?.map((item, index) => (
                                    <Text size="paragraph" mono={true} key={index} classes="pad-xs border-bottom border-right height-100">{item.inventory_item?.manufacturer || ""}</Text>
                                ))}
                            </div>
                            <div className="flex vertical grow-1 height-100">
                                <Text size="caption" mono={true} bold={true} classes="pad-xs border-bottom border-right">MPN</Text>
                                {keystoneTrayData?.related_offer_items?.map((item, index) => (
                                    <Text size="paragraph" mono={true} key={index} classes="pad-xs border-bottom border-right height-100">{item.inventory_item?.mpn || ""}</Text>
                                ))}
                            </div>
                            <div className="flex vertical height-100">
                                <Text size="caption" mono={true} bold={true} classes="pad-xs border-bottom border-right">BIN</Text>
                                {keystoneTrayData?.related_offer_items?.map((item, index) => (
                                    <Text size="paragraph" mono={true} key={index} classes="pad-xs border-bottom border-right height-100">{item.inventory_item?.bin_location || ""}</Text>
                                ))}
                            </div>
                            <div className="flex vertical height-100">
                                <Text size="caption" mono={true} bold={true} classes="pad-xs border-bottom">QTY</Text>
                                {keystoneTrayData?.related_offer_items?.map((item, index) => (
                                    <Text size="paragraph" mono={true} key={index} classes="pad-xs border-bottom height-100">{item.quantity || ""}</Text>
                                ))}
                            </div>
                        </div>
                        <div className="flex vertical align-end">
                            <Text size="paragraph" mono={true} bold={true} classes="pad-xs">Total Items: {totalItems}</Text>
                        </div>
                    </div>
                    <div className="flex horizontal border-top align-center gap-xl">
                        <img src={LogoBlack} alt="Materialize Logo" className="logo-black pad-s" />
                        <Text size="micro" mono={true} bold={true} classes="pad-xs">Have excess inventory? We've got you covered. Visit trymaterialize.com or email us at info@trymaterialize.com!</Text>
                    </div>
                </div>
            </div>
        );
    };

    const printPage = useCallback(() => window.print(), []);

    return keystoneTrayData && (
        <div className="keystone-offer-details grey-bg product-vertical-container flex-1 width-100">
            <div className="keystone-tray-header black-bg product-vertical-container width-100 space-between pad-l v-gap-display" style={{
                // mixBlendMode: "difference",
                // backgroundColor: (() => {
                // const { hue, sat, light } = colorFromNumber(keystoneTrayData.offer?.offer_number);
                // return `hsl(${hue}, ${sat}%, ${light}%)`;
                // })()
            }}>
                <div className="flex horizontal  gap-xl space-between">
                    <Button
                        label="Dismiss"
                        onClick={handleCloseTray}
                        textColor="white"
                        onBrand={true} />
                    <Text size="caption" bold={true} variant="white" classes="align-end ellipsis" mono={true}>Shipment: {keystoneTrayData.shipment_number.toString().padStart(12,'0').replace(/(\d{4})(\d{4})(\d{4})/, '$1-$2-$3')}</Text>
                </div>
                <div className="product-vertical-container gap-m">
                    <div className="flex vertical gap-s">
                        <div className="flex vertical">
                            <Text size="paragraph" bold={true} variant="white">{totalItems} Items</Text>
                            <Text size="h1" bold={true} variant="yellow" mono={true}>{keystoneTrayData?.warehouse_name}</Text>
                        </div>
                        <Text size="caption" variant="white" mono={true} bold={true}>Destination: {keystoneTrayData?.offer?.offer_region}, {keystoneTrayData?.offer?.offer_country}</Text>
                    </div>
                </div>
            </div>
            <div className="product-vertical-container flex-1 width-100 overflow-auto">
                <div className="flex vertical pad-m flex-1 overflow-auto gap-xl">
                    <div className="keystone-card flex vertical">
                        <div className="flex vertical">
                            <div className="flex horizontal pad-m gap-m space-between">
                                <Text bold={true} size="h6">Pick List</Text>
                                <NewButton variant="secondary" icon="printer" size="medium" onClick={printPage}>Print Pick List</NewButton>
                            </div>
                            <Divider />
                        </div>
                        <div className="flex vertical pad-m gap-s">
                            {keystoneTrayData?.related_offer_items?.map((item, index) => (
                                <Navigate key={index} unsetHeight={true} unsetPadding={true} variant="grey" classes="pad-xl" href={`/inventory?id=${item.inventory_item?.id}`}>
                                    <div className="flex horizontal gap-m space-between flex-wrap grow-1">
                                        <div className="flex horizontal v-gap-m align-center space-between grow-1">
                                            <div className="flex vertical align-start">
                                                <Text size="caption" variant="secondary" bold={true}>{item.inventory_item?.manufacturer}</Text>
                                                <Text size="paragraph" bold={true} mono={true}>{item.inventory_item?.mpn}</Text>
                                            </div>
                                        </div>
                                        {item?.inventory_item?.bin_location && (
                                            <div className="flex shrink-1 vertical white-bg pad-xs round-s">
                                                <Text size="caption" variant="secondary" bold={true}>Bin Location</Text>
                                                <Text size="paragraph" bold={true} mono={true}>{item.inventory_item?.bin_location}</Text>
                                            </div>
                                        )}
                                        <div className="flex horizontal flex-wrap shrink-1 gap-m">
                                            <div className="flex grow-1 vertical white-bg pad-xs round-s">
                                                <Text size="caption" variant="secondary" bold={true}>Quantity</Text>
                                                <Text size="paragraph" bold={true} mono={true}>{formatNumberWithCommas(item.quantity)}</Text>
                                            </div>
                                        </div>
                                    </div>
                                </Navigate>
                            ))}
                        </div>
                    </div>
                    {keystoneTrayData.tracking?.length > 0 ? (
                        <div className="keystone-card flex vertical">
                            <div className="pad-m">
                                <Text bold={true} size="title">Tracking</Text>
                            </div>
                            <Divider />
                            <div className="flex vertical pad-m gap-s">
                                {keystoneTrayData.tracking?.map((tracking, index) => (
                                    <div className="flex grey-bg round-s pad-s horizontal flex-wrap gap-s" key={index}>
                                        <Navigate href={tracking.shipment_tracking_url} icon="external" variant="secondary" size="large" newTab={true} classes="pad-m space-between" style={{flexGrow: "1"}}>
                                            <Text size="paragraph" mono={true} bold={true}>{tracking?.shipment_tracking_carrier || 'UPS'}: {tracking?.shipment_tracking_number}</Text>
                                        </Navigate>
                                        <NewButton onClick={() => downloadFile(tracking.shipping_label_id)} style={{flexGrow: "1"}} variant={keystoneTrayData.status !== 'PENDING' ? "secondary" : "primary"} size="large" icon="printer" classes="space-between">Download Shipping Label</NewButton>
                                    </div>
                                ))}
                            </div>
                        </div>
                    ) : (
                        <div className="keystone-card flex vertical">
                            <div className="pad-m">
                                <Text bold={true} size="title">Tracking</Text>
                            </div>
                            <Divider />
                            <div className="flex vertical pad-m gap-s">
                                <div className="flex vertical warning-bg pad-s gap-xs round-m">
                                    <Text variant="warning" size="paragraph" bold={true}>We’re getting your label ready!</Text>
                                    <Text variant="warning" size="caption">You will receive an email when your shipping label is ready for download.</Text>
                                </div>
                            </div>
                        </div>
                    )}
                    <div className="keystone-card flex vertical">
                        <div className="pad-m">
                            <Text bold={true} size="title">Offer</Text>
                        </div>
                        <Divider />
                        <div className="flex vertical pad-m gap-s">
                            <Navigate href={
                                `/offers/${keystoneTrayData.offer?.payout_status === "SENT" ? "completed" : "accepted"}?id=${keystoneTrayData.offer?.id}`
                            } key={keystoneTrayData.offer?.id} classes="width-100" variant="grey" size="large" unsetHeight={true} unsetPadding={true}>
                                <div className="flex horizontal gap-xl width-100 space-between align-center">
                                    <Text size="paragraph" mono={true} bold={true}>{formatDisplayNumber(keystoneTrayData?.offer?.offer_number)}</Text>
                                    <div className="status-chip flex horizontal white-bg round-s gap-m">
                                        <div class={`small-circle circle-positive`}/>
                                        <Text size="micro" bold={true} mono={true}>{keystoneTrayData.offer?.payout_status === "SENT" ? "Completed" : keystoneTrayData.offer?.status === "ACCEPTED" ? "Accepted" : "Completed"}</Text>
                                    </div>
                                </div>
                            </Navigate>
                        </div>
                    </div>
                    {keystoneTrayData.offer?.shipments?.filter((shipment) => shipment.id !== keystoneTrayData.id).length > 0 && (
                        <div className="keystone-card flex vertical">
                            <div className="pad-m">
                                <Text bold={true} size="title">Related Shipment{keystoneTrayData.offer?.shipments?.filter((shipment) => shipment.id !== keystoneTrayData.id).length > 1 ? "s" : ""}</Text>
                            </div>
                            <Divider />
                            <div className="flex vertical pad-m gap-s">
                                {keystoneTrayData.offer?.shipments?.filter((shipment) => shipment.id !== keystoneTrayData.id).map((shipment, index) => (
                                    <Navigate href={
                                        `/shipments/${shipment.status === "PENDING" ? "awaiting-shipment" : shipment.status === "SHIPPED" ? "shipped" : "delivered"}?id=${shipment.id}`
                                    } key={shipment.id} classes="width-100" variant="grey" size="large" unsetHeight={true} unsetPadding={true}>
                                        <div className="flex horizontal gap-xl width-100 space-between align-center">
                                            <Text size="paragraph" mono={true} bold={true}>{formatDisplayNumber(shipment.shipment_number)}</Text>
                                            <div className="status-chip flex horizontal white-bg round-s gap-m">
                                                <div class={`small-circle circle-${shipment.status === "PENDING" ? "negative" : shipment.status === "SHIPPED" ? "warning" : "positive"}`}/>
                                                <Text size="micro" bold={true} mono={true}>{shipment.status === "PENDING" ? "Awaiting Shipment" : shipment.status === "SHIPPED" ? "Shipped" : "Delivered"}</Text>
                                            </div>
                                        </div>
                                    </Navigate>
                                ))}
                            </div>
                        </div>
                    )}
                    {keystoneTrayData.offer?.payouts?.length > 0 && (
                        <div className="keystone-card flex vertical gap-xs">
                            <div className="pad-m">
                                <Text bold={true} size="title">Payout{keystoneTrayData.offer?.payouts?.length > 1 ? "s" : ""}</Text>
                            </div>
                            <Divider />
                            <div className="flex vertical pad-m gap-s">
                                {keystoneTrayData.offer?.payouts?.map((payout, index) => (
                                    <Navigate href={
                                        `/payouts/${payout.status === "ON_HOLD" ? "on-hold" : "sent"}?id=${payout.id}`
                                    } key={payout.id} classes="width-100" variant="grey" size="large" unsetHeight={true} unsetPadding={true}>
                                        <div className="flex horizontal gap-xl width-100 space-between align-center">
                                            <Text size="paragraph" mono={true} bold={true}>{formatDisplayNumber(payout.payout_number)}</Text>
                                            <div className="status-chip flex horizontal white-bg round-s gap-m">
                                                <div class={`small-circle circle-${payout.status === "ON_HOLD" ? "warning" : "positive"}`}/>
                                                <Text size="micro" bold={true} mono={true}>{payout.status === "ON_HOLD" ? "On Hold" : "Sent"}</Text>
                                            </div>
                                        </div>
                                    </Navigate>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
                <div className="product-vertical-container white-bg box-shadow-up tray-bottom">
                    <div className="product-horizontal-container justify-end pad-l h-gap-m">
                        {keystoneTrayData.status !== 'PENDING' ? (
                            <NewButton onClick={() => handleReject(keystoneTrayType)} disabled={true} style={{flexGrow: "1"}} size="large" icon="check-square" classes="space-between">Shipped</NewButton>
                        ) : (
                            <NewButton onClick={() => handleMarkAsShipped(keystoneTrayData?.id)} style={{flexGrow: "1"}} size="large" icon="check-square" classes="space-between" variant="secondary" disabled={keystoneTrayData?.tracking?.length === 0}>Mark Shipped</NewButton>
                        )}
                    </div>
                </div>
            </div>
            <PickList />
        </div>
    );
};

export default React.memo(ShipmentDetails);